/**
 * Gift Box Customizer Modal - Two-Up Edition
 * Two-up preview (desktop) + segmented control (mobile) + sidebar editor
 */

(() => {
  // Snowcubes Gift Card Core (loaded globally via `layout/theme.liquid`)
  const Gift = window.SnowcubesGift || {};

  // The modal is demand-loaded, but the theme shell emits this locale-aware
  // payload before the loader runs. Keep every customer-facing control keyed so
  // Shopify Markets can switch the composer without translating user-entered
  // content or card IDs.
  function translate(key, values, fallback) {
    const table = window.SnowcubesLocale || {};
    let value = table[key];
    if (typeof value !== 'string' || !value) value = fallback || key;
    // Shopify's `t` filter escapes text before the shell serializes it as JSON.
    // Decode that single layer without parsing HTML; DOM sinks still escape
    // markup or use textContent, and interpolated customer text stays untouched.
    const entities = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&#x27;': "'" };
    value = value.replace(/&(?:amp|lt|gt|quot|#39|#x27);/g, (entity) => entities[entity]);
    return value.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_match, name) => (
      values && values[name] !== undefined ? String(values[name]) : ''
    ));
  }

  function localizedCardLabel(card) {
    return translate(`pages.gift_modal.cards.${card?.id}.label`, null, card?.label || '');
  }

  function localizedCardImageAlt(card) {
    return translate(`pages.gift_modal.cards.${card?.id}.image_alt`, null, card?.imageAlt || localizedCardLabel(card));
  }

  function localizedCardPreset(card, type, index, value) {
    return translate(`pages.gift_modal.cards.${card?.id}.${type}_${index}`, null, value || '');
  }

  function localizedStarter(starter) {
    if (!starter) return starter;
    return {
      ...starter,
      label: translate(`pages.gift_modal.starters.${starter.id}.label`, null, starter.label),
      message: translate(`pages.gift_modal.starters.${starter.id}.message`, null, starter.message),
    };
  }

  function trackGift(eventKey, properties = {}) {
    const analytics = window.SnowcubesAnalytics;
    const eventName = analytics?.EVENTS?.[eventKey];
    if (!eventName || typeof analytics.track !== 'function') return;
    analytics.track(eventName, properties);
  }

  function messageLengthBucket(value) {
    const length = typeof value === 'string' ? value.trim().length : 0;
    if (length === 0) return 'empty';
    if (length <= 50) return '1_50';
    if (length <= 120) return '51_120';
    return '121_200';
  }

  // Card catalog (loaded via gift-card-catalog.js before this file)
  const Catalog = window.SnowcubesGiftCatalog || {};
  const CARD_CATALOG = Catalog.CARD_CATALOG || [];
  const CARD_CATALOG_BY_ID = Catalog.CARD_CATALOG_BY_ID || {};
  const CARD_PICKER_CATALOG = Catalog.CARD_PICKER_CATALOG || CARD_CATALOG.filter((card) => !card.hidden);
  const DEFAULT_CARD_ID = Catalog.DEFAULT_CARD_ID || 'knicks_2026_the_leap';
  const FONT_OPTIONS = Catalog.FONT_OPTIONS || [];
  const DEFAULT_FONT_ID = Catalog.DEFAULT_FONT_ID || 'serif';
  const GREETING_MAX_LENGTH = Catalog.GREETING_MAX_LENGTH || 30;
  const CLOSING_MAX_LENGTH = Catalog.CLOSING_MAX_LENGTH || 20;
  const normalizeFontId = Catalog.normalizeFontId || ((id) => id || DEFAULT_FONT_ID);

  const CARD_IMAGE_WIDTHS = {
    cover: [160, 240, 320, 480],
    front: [240, 320, 480],
    summary: [96, 160],
  };
  const CARD_IMAGE_FALLBACK_WIDTH = {
    cover: 320,
    front: 480,
    summary: 96,
  };
  const CARD_IMAGE_SIZES = {
    cover: '(max-width: 768px) 160px, 220px',
    front: '(max-width: 900px) 320px, 320px',
    summary: '28px',
  };
  const TRANSPARENT_IMAGE_SRC = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';

  function withImageWidth(url, width) {
    if (!url || !width) return url || '';
    try {
      const parsed = new URL(url, window.location.href);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (_) {
      const cleanUrl = url
        .replace(/([?&])width=\d+(&?)/, (match, prefix, suffix) => (suffix ? prefix : ''))
        .replace(/[?&]$/, '');
      return `${cleanUrl}${cleanUrl.includes('?') ? '&' : '?'}width=${width}`;
    }
  }

  function getCardImageUrl(card, variant) {
    const width = CARD_IMAGE_FALLBACK_WIDTH[variant] || CARD_IMAGE_FALLBACK_WIDTH.cover;
    return withImageWidth(card?.imageUrl, width);
  }

  function getCardImageSrcset(card, variant) {
    const widths = CARD_IMAGE_WIDTHS[variant] || CARD_IMAGE_WIDTHS.cover;
    return widths.map((width) => `${withImageWidth(card?.imageUrl, width)} ${width}w`).join(', ');
  }

  function getCardImageAttributes(card, variant, defer = true) {
    if (!card?.imageUrl) return 'src=""';
    if (defer) {
      return [
        `src="${TRANSPARENT_IMAGE_SRC}"`,
        `data-src="${getCardImageUrl(card, variant)}"`,
        `data-srcset="${getCardImageSrcset(card, variant)}"`,
        `data-sizes="${CARD_IMAGE_SIZES[variant] || CARD_IMAGE_SIZES.cover}"`,
        'loading="lazy"',
        'decoding="async"',
        'fetchpriority="low"',
      ].join(' ');
    }
    return [
      `src="${getCardImageUrl(card, variant)}"`,
      `srcset="${getCardImageSrcset(card, variant)}"`,
      `sizes="${CARD_IMAGE_SIZES[variant] || CARD_IMAGE_SIZES.cover}"`,
      'loading="lazy"',
      'decoding="async"',
      'fetchpriority="low"',
    ].join(' ');
  }

  function applyDeferredCardImage(img, card, variant) {
    if (!img || !card?.imageUrl) return;
    img.src = TRANSPARENT_IMAGE_SRC;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.dataset.src = getCardImageUrl(card, variant);
    img.dataset.srcset = getCardImageSrcset(card, variant);
    img.dataset.sizes = CARD_IMAGE_SIZES[variant] || CARD_IMAGE_SIZES.cover;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.setAttribute('fetchpriority', 'low');
  }

  function applyCardImage(img, card, variant) {
    if (!img || !card?.imageUrl) return;
    img.src = getCardImageUrl(card, variant);
    img.srcset = getCardImageSrcset(card, variant);
    img.sizes = CARD_IMAGE_SIZES[variant] || CARD_IMAGE_SIZES.cover;
    delete img.dataset.src;
    delete img.dataset.srcset;
    delete img.dataset.sizes;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.setAttribute('fetchpriority', 'low');
  }

// ── Backwards-compatible fallbacks ──
// These only run when gift-card-core.js hasn't set the globals first.
// In practice, core always loads before this file because both use `defer`
// and core's <script> appears earlier in layout/theme.liquid (line ~39 vs
// the modal snippet at line ~126). So these branches are dead code under
// normal operation — they exist purely as a safety net for edge cases
// (e.g. core fails to load due to a network error).
if (!window.isGiftUIEnabled) {
  // Fallback: delegate to centralized SnowcubesFlags if available.
  window.isGiftUIEnabled = function isGiftUIEnabledFallback() {
    return true;
  };
}

// Fallback giftConfigPrototype — only used when gift-card-core.js fails to
// load (see load-order note above). Dead code under normal operation.
if (!window.giftConfigPrototype) {
  const GIFT_CONFIG_KEY_PREFIX = 'sc_gift_config_prototype';
  const getHandleFromPath = () => {
    try {
      const m = location.pathname.match(/\/products\/([^/?#]+)/);
      return m ? m[1] : null;
    } catch (_) { return null; }
  };
  const getKey = (productHandle) => {
    const h =
      (typeof productHandle === 'string' && productHandle.trim()) ||
      getHandleFromPath();
    return h ? `${GIFT_CONFIG_KEY_PREFIX}:${h}` : GIFT_CONFIG_KEY_PREFIX;
  };

  window.giftConfigPrototype = {
    save(config, productHandle) {
      try {
        localStorage.setItem(getKey(productHandle), JSON.stringify(config));
        return true;
      } catch (e) {
        return false;
      }
    },
    load(productHandle) {
      try {
        const stored = localStorage.getItem(getKey(productHandle));
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    },
    clear(productHandle) {
      try {
        localStorage.removeItem(getKey(productHandle));
        return true;
      } catch (e) {
        return false;
      }
    }
  };
}

class GiftBoxCustomizerModal extends HTMLElement {
  constructor() {
    super();
    
    this.isOpen = false;
    this.activePreviewSide = 'front';
    this.mode = 'add';
    this.step = 'pick-card'; // 'pick-card' | 'write-message'
    this.variantId = null;
    this.analyticsVariantId = null;
    this.itemKey = null; // Prefer itemKey for cart updates (most stable identifier)
    this.resumeProductForm = null;
    this.resumeSubmitter = null;
    this.resumeAddToCart = false;
    this.entryAction = 'customize_button';

    // Form state
    this.selectedCardId = DEFAULT_CARD_ID;
    this.matchedCardId = null;
    this.selectedGreeting = '';
    this.selectedClosing = '';
    this.recipientName = '';
    this.noteText = '';
    this.lastStarterId = null;
    this.senderName = '';
    this.selectedFont = DEFAULT_FONT_ID;
    this.maxChars = 200;
    
    // Resilience state
    this.isSaving = false;
    this.lastSubmitTime = 0;
    this.submitDebounceMs = 400;
    this.saveTimeoutMs = 10000;
    this.previouslyFocusedElement = null;
    this.analyticsCompleted = false;
    this._savedScrollY = 0;
    this._baseViewportHeight = 0;
    this._inertTarget = null;
    this._scrollCleanup = null;

    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.switchPreviewSide = this.switchPreviewSide.bind(this);
    this.handleCardChange = this.handleCardChange.bind(this);
    this.handleRecipientInput = this.handleRecipientInput.bind(this);
    this.handleNoteInput = this.handleNoteInput.bind(this);
    this.handleSenderInput = this.handleSenderInput.bind(this);
    this.handleFontChange = this.handleFontChange.bind(this);
    this.handleGreetingChange = (e) => this.handlePresetChange('greeting', e);
    this.handleClosingChange = (e) => this.handlePresetChange('closing', e);
    this.handleStarterClick = this.handleStarterClick.bind(this);
    this.confirmCard = this.confirmCard.bind(this);
    this.changeCard = this.changeCard.bind(this);
    this.handleHandwrite = this.handleHandwrite.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleViewportResize = this.handleViewportResize.bind(this);
  }
  
  connectedCallback() {
    this.render();
    this.attachEventListeners();

    // Persistent image node for the summary icon — created once, updated in updatePreview().
    this._summaryImg = document.createElement('img');
    this._summaryImg.className = 'gift-modal__card-summary-img';
  }
  
  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    this._scrollCleanup?.();
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleViewportResize);
      window.visualViewport.removeEventListener('scroll', this.handleViewportResize);
    }
  }

  // Analytics may join the gift selection to the customizer without seeing
  // customer-authored content. Cart edits already provide a variant id; PDP
  // entries can resolve the owning form through the trigger or the intercepted
  // form that will receive the saved configuration. If neither is available,
  // omit the property rather than guessing from a document-wide form.
  getAnalyticsVariantId() {
    const explicitAnalyticsVariantId = Number(this.analyticsVariantId);
    if (Number.isInteger(explicitAnalyticsVariantId) && explicitAnalyticsVariantId > 0) {
      return explicitAnalyticsVariantId;
    }

    const explicitVariantId = Number(this.variantId);
    if (Number.isInteger(explicitVariantId) && explicitVariantId > 0) {
      return explicitVariantId;
    }

    let productForm = this.resumeProductForm?.isConnected ? this.resumeProductForm : null;
    if (!productForm && this.callerTrigger?.resolveProductForm) {
      try {
        productForm = this.callerTrigger.resolveProductForm();
      } catch (_) {
        productForm = null;
      }
    }
    if (!(productForm instanceof HTMLFormElement)) return null;

    const idField = productForm.elements?.namedItem?.('id') || productForm.querySelector('[name="id"]');
    const resolvedVariantId = Number(idField?.value);
    return Number.isInteger(resolvedVariantId) && resolvedVariantId > 0 ? resolvedVariantId : null;
  }

  trackGiftAnalytics(eventKey, properties = {}) {
    const variantId = this.getAnalyticsVariantId();
    trackGift(eventKey, {
      ...properties,
      ...(variantId !== null ? { variant_id: variantId } : {}),
    });
  }

  // Centralized cleanup for all inline styles set by handleViewportResize.
  // Called from open(), close(), and the keyboard-closed branch.
  _clearKeyboardStyles() {
    const overlay = this.querySelector('#gift-modal-overlay');
    const modal = this.querySelector('.gift-modal');
    const footer = this.querySelector('.gift-modal__footer');
    if (overlay) overlay.style.transform = '';
    if (modal) modal.style.height = '';
    if (footer) footer.style.display = '';
  }
  
  render() {
    const defaultCard = CARD_CATALOG_BY_ID[DEFAULT_CARD_ID] || CARD_PICKER_CATALOG[0] || CARD_CATALOG[0];

    const cardOptionsHtml = CARD_PICKER_CATALOG.map((card) => {
      const cardLabel = localizedCardLabel(card);
      const cardGreeting = localizedCardPreset(card, 'greeting', 0, card.greetingPresets?.[0]);
      const visualHtml = card.imageUrl
        ? `<img class="gift-modal__cover-img" ${getCardImageAttributes(card, 'cover')} data-card-image-id="${card.id}" data-card-image-variant="cover" alt="${this.escapeAttr(localizedCardImageAlt(card))}">`
        : '';
      return `
      <label class="gift-modal__cover-option">
        <input type="radio" name="cardId" value="${card.id}" ${card.id === DEFAULT_CARD_ID ? 'checked' : ''}>
        <div class="gift-modal__cover-label${card.imageUrl ? ' has-art' : ''}" data-card-id="${card.id}">
          ${visualHtml}
          <span class="gift-modal__cover-name">${this.escapeHtml(cardLabel)}</span>
          <span class="gift-modal__cover-greeting">${this.escapeHtml(cardGreeting)}</span>
        </div>
      </label>`;
    }).join('');

    const fontOptionsHtml = FONT_OPTIONS.map((font) => `
      <label class="gift-modal__font-option">
        <input type="radio" name="font" value="${font.id}" ${font.id === DEFAULT_FONT_ID ? 'checked' : ''}>
        <div class="gift-modal__font-label">
          <span class="gift-modal__font-preview ${font.previewClass}">Aa</span>
          <span class="gift-modal__font-name">${translate(`pages.gift_modal.fonts.${font.id}`, null, font.label)}</span>
        </div>
      </label>
    `).join('');

    const defaultCardLabel = localizedCardLabel(defaultCard);
    const defaultCardImageAlt = localizedCardImageAlt(defaultCard);
    const defaultCardGreeting = localizedCardPreset(defaultCard, 'greeting', 0, defaultCard.greetingPresets?.[0]);
    const defaultCardClosing = localizedCardPreset(defaultCard, 'closing', 0, defaultCard.closingPresets?.[0]);

    this.innerHTML = `
      <div class="gift-modal-overlay" id="gift-modal-overlay">
        <div class="gift-modal" role="dialog" aria-modal="true" aria-labelledby="gift-modal-title">

          <!-- Header -->
          <div class="gift-modal__header">
            <h2 class="gift-modal__title" id="gift-modal-title">
              <span class="gift-modal__title-text">${translate('pages.gift_modal.title_add')}</span>
            </h2>
            <span class="gift-modal__step-indicator" id="step-indicator" hidden>${translate('pages.gift_modal.step_indicator', { current: 1, total: 2 })}</span>
            <button type="button" class="gift-modal__close" aria-label="${translate('pages.gift_modal.close')}" id="gift-modal-close">
              ✕
            </button>
          </div>

          <!-- Body -->
          <div class="gift-modal__body">

            <!-- Left: Card Preview -->
            <div class="gift-modal__preview-area">

              <!-- Mobile Segmented Control -->
              <div class="gift-modal__tabs" role="tablist" aria-label="${translate('pages.gift_modal.card_preview')}">
                <button
                  type="button"
                  class="gift-modal__tab is-active"
                  role="tab"
                  aria-selected="true"
                  aria-controls="preview-front-panel"
                  data-preview-tab="front"
                  id="tab-front"
                >
                  ${translate('pages.gift_modal.front')}
                </button>
                <button
                  type="button"
                  class="gift-modal__tab"
                  role="tab"
                  aria-selected="false"
                  aria-controls="preview-back-panel"
                  data-preview-tab="back"
                  id="tab-back"
                >
                  ${translate('pages.gift_modal.back')}
                </button>
              </div>

              <!-- Two-Up Preview (both visible on desktop, one at a time on mobile) -->
              <div class="gift-modal__previews">

                <!-- Front Preview (readonly) -->
                <div
                  class="gift-modal__preview-card gift-modal__preview-front is-active"
                  id="preview-front-panel"
                  role="tabpanel"
                  aria-labelledby="tab-front"
                  data-preview-panel="front"
                >
                  <div class="gift-modal__preview-label">${translate('pages.gift_modal.front')}</div>
                  <div class="gift-modal__card-face gift-modal__card-front is-${DEFAULT_CARD_ID}" id="card-front">
                    <div class="gift-modal__card-front-content">
                      <img class="gift-modal__card-front-art" id="front-art"
                        ${getCardImageAttributes(defaultCard, 'front')} alt="${this.escapeAttr(defaultCardImageAlt)}"
                        ${defaultCard.imageUrl ? '' : 'hidden'}
                        style="${defaultCard.imageStyle ? Object.entries(defaultCard.imageStyle).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}:${v}`).join(';') : ''}">
                      <div class="gift-modal__card-front-greeting" id="front-greeting">${this.escapeHtml(defaultCardGreeting)}</div>
                      <div class="gift-modal__card-front-subtext" id="front-closing">${this.escapeHtml(defaultCardClosing)}</div>
                    </div>
                  </div>
                </div>

                <!-- Back Preview (read-only) -->
                <div
                  class="gift-modal__preview-card gift-modal__preview-back"
                  id="preview-back-panel"
                  role="tabpanel"
                  aria-labelledby="tab-back"
                  data-preview-panel="back"
                >
                  <div class="gift-modal__preview-label">${translate('pages.gift_modal.back')}</div>
                  <div class="gift-modal__card-face gift-modal__card-back" id="card-back">
                    <div class="gift-modal__card-back-content">
                      <div class="gift-modal__card-recipient" hidden>
                        ${translate('pages.gift_modal.to')}: <span class="gift-modal__card-recipient-name" id="preview-recipient"></span>
                      </div>
                      <div class="gift-modal__card-message is-serif" id="preview-message">
                        <span class="gift-modal__card-message-placeholder">${translate('pages.gift_modal.note_placeholder')}</span>
                      </div>
                      <div class="gift-modal__card-signature" id="preview-sender" hidden></div>
                    </div>
                  </div>
                </div>

              </div>
              <p class="gift-modal__illustrator-credit" id="illustrator-credit"${defaultCard.artist ? '' : ' hidden'}>${defaultCard.artist ? `${translate('pages.gift_modal.illustrated_by')} ${defaultCard.artist.url ? `<a href="${defaultCard.artist.url}" target="_blank" rel="noopener" class="gift-modal__illustrator-link">${defaultCard.artist.name}</a>` : defaultCard.artist.name}` : ''}</p>
            </div>

            <!-- Right: Editor Sidebar -->
            <div class="gift-modal__editor" aria-live="polite">
              <div class="gift-modal__editor-scroll">

                <!-- ======= STEP 1: Pick your card ======= -->
                <div class="gift-modal__step gift-modal__step-card is-active-step" data-step="pick-card">
                  <div class="gift-modal__section-header">
                    <h3 class="gift-modal__section-title gift-modal__section-title--step">${translate('pages.gift_modal.pick_card')}</h3>
                  </div>
                  <p class="gift-modal__helper-copy">${translate('pages.gift_modal.pick_card_help')}</p>

                  <div class="gift-modal__covers" data-covers role="radiogroup" aria-label="${translate('pages.gift_modal.card_design')}">
                    ${cardOptionsHtml}
                  </div>
                </div>

                <!-- ======= STEP 2: Write your message ======= -->
                <div class="gift-modal__step gift-modal__step-message" data-step="write-message">
                  <!-- Summary row (collapsed card choice) -->
                  <button type="button" class="gift-modal__card-summary" id="card-summary" aria-label="${this.escapeAttr(translate('pages.gift_modal.card_selected', { card: defaultCardLabel, action: translate('pages.gift_modal.choose_another_design') }))}">
                    <span class="gift-modal__card-summary-visual" id="summary-icon">${defaultCard.imageUrl ? `<img class="gift-modal__card-summary-img" ${getCardImageAttributes(defaultCard, 'summary')} alt="${this.escapeAttr(defaultCardImageAlt)}">` : ''}</span>
                    <span class="gift-modal__card-summary-text">
                      <span id="summary-label">${this.escapeHtml(defaultCardLabel)}</span>
                    </span>
                    <span class="gift-modal__card-summary-action">${translate('pages.gift_modal.choose_another_design')}</span>
                  </button>
                  <p class="gift-modal__quantity-note" data-gift-quantity-note hidden></p>
                  <!-- The note is the job. Optional helpers stay collapsed below it. -->
                  <div class="gift-modal__section">
                    <div class="gift-modal__input-group">
                      <label class="gift-modal__field-label" for="recipient-name">${translate('pages.gift_modal.to')}</label>
                      <input
                        type="text"
                        class="gift-modal__input"
                        id="recipient-name"
                        placeholder="${translate('pages.gift_modal.recipient_placeholder')}"
                        maxlength="50"
                      >
                    </div>

                    <div class="gift-modal__input-group gift-modal__field-spacer">
                      <label class="gift-modal__field-label" for="gift-note">${translate('pages.gift_modal.message')}</label>
                      <div class="gift-modal__note-wrapper">
                        <textarea
                          class="gift-modal__note"
                          id="gift-note"
                          placeholder="${translate('pages.gift_modal.note_placeholder_input')}"
                          maxlength="${this.maxChars}"
                          aria-describedby="char-count"
                        ></textarea>
                        <div class="gift-modal__char-count" id="char-count" aria-live="polite">0/${this.maxChars}</div>
                      </div>
                    </div>

                    <div class="gift-modal__input-group gift-modal__field-spacer">
                      <label class="gift-modal__field-label" for="sender-name">${translate('pages.gift_modal.from')} <span class="gift-modal__label-hint">(${translate('pages.gift_modal.optional')})</span></label>
                      <input
                        type="text"
                        class="gift-modal__input"
                        id="sender-name"
                        placeholder="${translate('pages.gift_modal.sender_placeholder')}"
                        maxlength="50"
                      >
                    </div>
                  </div>

                  <details class="gift-modal__disclosure" data-occasion-starters>
                    <summary>${translate('pages.gift_modal.use_message_starter')}</summary>
                    <div class="gift-modal__preset-chips" data-starter-group></div>
                  </details>

                  <details class="gift-modal__disclosure">
                    <summary>${translate('pages.gift_modal.note_styling')}</summary>
                    <div class="gift-modal__section">
                      <label class="gift-modal__field-label">${translate('pages.gift_modal.font')}</label>
                      <div class="gift-modal__fonts" role="radiogroup" aria-label="${translate('pages.gift_modal.font_style')}">
                        ${fontOptionsHtml}
                      </div>

                      <label class="gift-modal__field-label gift-modal__field-spacer">${translate('pages.gift_modal.headline')}</label>
                      <div class="gift-modal__preset-chips" data-preset-group="greeting"></div>

                      <label class="gift-modal__field-label gift-modal__field-spacer">${translate('pages.gift_modal.accent_line')}</label>
                      <div class="gift-modal__preset-chips" data-preset-group="closing"></div>
                    </div>
                  </details>
                </div>

              </div>

              <!-- Footer Actions -->
              <div class="gift-modal__footer">
                <button type="button" class="gift-modal__btn gift-modal__btn-handwrite __style-underline" id="gift-modal-handwrite" hidden>
                  ${translate('pages.gift_modal.handwrite')}
                </button>
                <button type="button" class="gift-modal__btn gift-modal__btn-submit" id="gift-modal-submit">
                  <span id="submit-btn-text">${translate('pages.gift_modal.write_message')}</span>
                </button>
              </div>
            </div>

          </div>

        </div>
      </div>
    `;

    // Populate greeting/closing preset chips for the default card
    this.renderPresetChips();
  }

  hydrateCardImages() {
    this.imagesHydrated = true;
    this.querySelectorAll('[data-card-image-id][data-card-image-variant]').forEach((img) => {
      const card = CARD_CATALOG_BY_ID[img.dataset.cardImageId];
      applyCardImage(img, card, img.dataset.cardImageVariant);
    });
    this.updatePreview();
  }

  renderPresetChips() {
    const card = CARD_CATALOG_BY_ID[this.selectedCardId] || CARD_CATALOG[0];

    // Auto-select first preset if nothing selected yet
    if (!this.selectedGreeting && card.greetingPresets?.length) {
      this.selectedGreeting = card.greetingPresets[0];
    }
    if (!this.selectedClosing && card.closingPresets?.length) {
      this.selectedClosing = card.closingPresets[0];
    }

    // Determine if current selection is custom (not in presets)
    const greetingIsCustom = this.selectedGreeting && !(card.greetingPresets || []).includes(this.selectedGreeting);
    const closingIsCustom = this.selectedClosing && !(card.closingPresets || []).includes(this.selectedClosing);

    const greetingContainer = this.querySelector('[data-preset-group="greeting"]');
    if (greetingContainer) {
      greetingContainer.innerHTML = (card.greetingPresets || []).map((text, index) => {
        const safeAttr = this.escapeAttr(text);
        const displayText = localizedCardPreset(card, 'greeting', index, text);
        const safeDisplayAttr = this.escapeAttr(displayText);
        const safeText = this.escapeHtml(displayText);
        const isSelected = !greetingIsCustom && this.selectedGreeting === text;
        return `
        <button type="button" class="gift-modal__chip${isSelected ? ' is-selected' : ''}"
          data-preset="greeting" data-value="${safeAttr}" aria-label="${this.escapeAttr(translate('pages.gift_modal.headline_aria_prefix'))}: ${safeDisplayAttr}"
          aria-pressed="${isSelected ? 'true' : 'false'}">
          ${safeText}
        </button>
      `;
      }).join('') + `
        <button type="button" class="gift-modal__chip gift-modal__chip--custom${greetingIsCustom ? ' is-selected' : ''}"
          data-preset="greeting" data-value="__custom__"
          aria-pressed="${greetingIsCustom ? 'true' : 'false'}">
          ${translate('pages.gift_modal.custom')}
        </button>
        <div class="gift-modal__custom-input-wrap${greetingIsCustom ? ' is-visible' : ''}" data-custom-wrap="greeting">
          <input type="text" class="gift-modal__custom-input" data-custom-input="greeting"
            placeholder="${this.escapeAttr(translate('pages.gift_modal.your_headline'))}" maxlength="${GREETING_MAX_LENGTH}"
            aria-label="${this.escapeAttr(translate('pages.gift_modal.custom_headline'))}"
            value="${greetingIsCustom ? this.escapeAttr(this.selectedGreeting) : ''}">
          <span class="gift-modal__custom-count" data-custom-count="greeting">${greetingIsCustom ? this.selectedGreeting.length : 0}/${GREETING_MAX_LENGTH}</span>
        </div>
      `;
    }

    const closingContainer = this.querySelector('[data-preset-group="closing"]');
    if (closingContainer) {
      closingContainer.innerHTML = (card.closingPresets || []).map((text, index) => {
        const safeAttr = this.escapeAttr(text);
        const displayText = localizedCardPreset(card, 'closing', index, text);
        const safeDisplayAttr = this.escapeAttr(displayText);
        const safeText = this.escapeHtml(displayText);
        const isSelected = !closingIsCustom && this.selectedClosing === text;
        return `
        <button type="button" class="gift-modal__chip${isSelected ? ' is-selected' : ''}"
          data-preset="closing" data-value="${safeAttr}" aria-label="${this.escapeAttr(translate('pages.gift_modal.accent_line_aria_prefix'))}: ${safeDisplayAttr}"
          aria-pressed="${isSelected ? 'true' : 'false'}">
          ${safeText}
        </button>
      `;
      }).join('') + `
        <button type="button" class="gift-modal__chip gift-modal__chip--custom${closingIsCustom ? ' is-selected' : ''}"
          data-preset="closing" data-value="__custom__"
          aria-pressed="${closingIsCustom ? 'true' : 'false'}">
          ${translate('pages.gift_modal.custom')}
        </button>
        <div class="gift-modal__custom-input-wrap${closingIsCustom ? ' is-visible' : ''}" data-custom-wrap="closing">
          <input type="text" class="gift-modal__custom-input" data-custom-input="closing"
            placeholder="${this.escapeAttr(translate('pages.gift_modal.your_accent_line'))}" maxlength="${CLOSING_MAX_LENGTH}"
            aria-label="${this.escapeAttr(translate('pages.gift_modal.custom_accent_line'))}"
            value="${closingIsCustom ? this.escapeAttr(this.selectedClosing) : ''}">
          <span class="gift-modal__custom-count" data-custom-count="closing">${closingIsCustom ? this.selectedClosing.length : 0}/${CLOSING_MAX_LENGTH}</span>
        </div>
      `;
    }

    // Attach input listeners for custom text fields
    this.querySelector('[data-custom-input="greeting"]')?.addEventListener('input', (e) => {
      this.selectedGreeting = e.target.value;
      const countEl = this.querySelector('[data-custom-count="greeting"]');
      if (countEl) countEl.textContent = `${e.target.value.length}/${GREETING_MAX_LENGTH}`;
      this.updatePreview();
    });
    this.querySelector('[data-custom-input="closing"]')?.addEventListener('input', (e) => {
      this.selectedClosing = e.target.value;
      const countEl = this.querySelector('[data-custom-count="closing"]');
      if (countEl) countEl.textContent = `${e.target.value.length}/${CLOSING_MAX_LENGTH}`;
      this.updatePreview();
    });

    this.updatePreview();
  }

  setStep(stepName) {
    this.step = stepName;

    // Toggle visibility with crossfade
    this.querySelectorAll('.gift-modal__step').forEach((el) => {
      el.classList.toggle('is-active-step', el.dataset.step === stepName);
    });

    // Reset scroll position so the new step starts from the top
    const scrollContainer = this.querySelector('.gift-modal__editor-scroll');
    if (scrollContainer) scrollContainer.scrollTop = 0;

    // Update footer CTA text
    const submitText = this.querySelector('#submit-btn-text');
    if (submitText) {
      if (stepName === 'pick-card') {
        submitText.textContent = translate('pages.gift_modal.write_message');
      } else {
        submitText.textContent =
          this.mode === 'edit'
            ? translate('pages.gift_modal.save_changes')
            : this.resumeAddToCart
              ? translate('pages.gift_modal.save_note_add_to_cart')
              : translate('pages.gift_modal.add_gift_note');
      }
    }

    const handwriteBtn = this.querySelector('#gift-modal-handwrite');
    if (handwriteBtn) {
      handwriteBtn.hidden = stepName !== 'write-message';
    }

    // Update step indicator
    const indicator = this.querySelector('#step-indicator');
    if (indicator) {
      indicator.hidden = true;
      indicator.textContent = '';
    }
  }

  confirmCard() {
    this.setStep('write-message');
    this.trackGiftAnalytics('GIFT_CUSTOMIZER_STEP_VIEWED', {
      gift_mode: this.mode,
      gift_step: 'compose',
    });
    // Re-render preset chips for the confirmed card
    this.renderPresetChips();
    this.renderOccasionStarters();
    this.updateMatchHelper();
    // Reset scroll after DOM settles (renderPresetChips triggers layout)
    requestAnimationFrame(() => {
      const scrollContainer = this.querySelector('.gift-modal__editor-scroll');
      if (scrollContainer) scrollContainer.scrollTop = 0;
    });
  }

  changeCard() {
    this.setStep('pick-card');
  }

  handlePresetChange(type, e) {
    const btn = e.target.closest(`.gift-modal__chip[data-preset="${type}"]`);
    if (!btn) return;

    const stateKey = type === 'greeting' ? 'selectedGreeting' : 'selectedClosing';
    const customWrap = this.querySelector(`[data-custom-wrap="${type}"]`);
    const customInput = this.querySelector(`[data-custom-input="${type}"]`);

    if (btn.dataset.value === '__custom__') {
      if (customWrap) customWrap.classList.add('is-visible');
      this[stateKey] = customInput?.value || '';
      if (customInput) setTimeout(() => customInput.focus(), 50);
    } else {
      this[stateKey] = btn.dataset.value;
      if (customWrap) customWrap.classList.remove('is-visible');
    }

    this.querySelectorAll(`.gift-modal__chip[data-preset="${type}"]`).forEach((chip) => {
      let selected;
      if (chip.dataset.value === '__custom__') {
        selected = btn.dataset.value === '__custom__';
      } else {
        selected = btn.dataset.value !== '__custom__' && chip.dataset.value === this[stateKey];
      }
      chip.classList.toggle('is-selected', selected);
      chip.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    this.updatePreview();
  }

  attachEventListeners() {
    this.querySelector('#gift-modal-close').addEventListener('click', () => this.close());
    
    this.querySelector('#gift-modal-overlay').addEventListener('click', (e) => {
      if (e.target.id === 'gift-modal-overlay') {
        this.close();
      }
    });
    
    // Mobile segmented control (tabs)
    this.querySelectorAll('[data-preview-tab]').forEach(tab => {
      tab.addEventListener('click', (e) => {
        const side = e.target.dataset.previewTab;
        this.switchPreviewSide(side);
      });
    });
    
    // Card selection
    this.querySelectorAll('input[name="cardId"]').forEach(input => {
      input.addEventListener('change', this.handleCardChange);
    });

    // Summary row (Step 2 → back to Step 1)
    const summaryBtn = this.querySelector('#card-summary');
    if (summaryBtn) {
      summaryBtn.addEventListener('click', this.changeCard);
    }

    // Greeting/closing preset chips (event delegation on containers)
    const greetingContainer = this.querySelector('[data-preset-group="greeting"]');
    if (greetingContainer) {
      greetingContainer.addEventListener('click', this.handleGreetingChange);
    }
    const closingContainer = this.querySelector('[data-preset-group="closing"]');
    if (closingContainer) {
      closingContainer.addEventListener('click', this.handleClosingChange);
    }
    const starterGroup = this.querySelector('[data-starter-group]');
    if (starterGroup) {
      starterGroup.addEventListener('click', this.handleStarterClick);
    }

    // Recipient input
    this.querySelector('#recipient-name').addEventListener('input', this.handleRecipientInput);
    
    // Note input
    this.querySelector('#gift-note').addEventListener('input', this.handleNoteInput);
    
    // Sender input
    this.querySelector('#sender-name').addEventListener('input', this.handleSenderInput);
    
    // Font selection
    this.querySelectorAll('input[name="font"]').forEach(input => {
      input.addEventListener('change', this.handleFontChange);
    });
    
    // Submit
    this.querySelector('#gift-modal-handwrite').addEventListener('click', this.handleHandwrite);
    this.querySelector('#gift-modal-submit').addEventListener('click', this.handleSubmit);
    
    // Keyboard
    document.addEventListener('keydown', this.handleKeydown);
    
    // iOS keyboard handling — wait for viewport to settle, then scroll
    const editorScroll = this.querySelector('.gift-modal__editor-scroll');
    if (editorScroll) {
      editorScroll.addEventListener('focusin', (e) => {
        if (!e.target.matches('input, textarea, select')) return;
        this._scrollToInput(e.target);
      });
      editorScroll.addEventListener('focusout', () => {
        requestAnimationFrame(this.handleViewportResize);
      });
    }
    
    // Visual viewport handling for iOS Safari keyboard.
    // `resize` fires when the keyboard opens/closes (height changes).
    // `scroll` fires when iOS shifts the visual viewport (offsetTop changes).
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleViewportResize);
      window.visualViewport.addEventListener('scroll', this.handleViewportResize);
    }
  }
  
  handleKeydown(e) {
    if (!this.isOpen) return;
    
    // Escape to close (unless saving)
    if (e.key === 'Escape') {
      if (this.isSaving) return;  // Don't interfere during save
      e.preventDefault();
      this.close();
      return;
    }
    
    // Tab trap - keep focus within modal
    if (e.key === 'Tab') {
      const modal = this.querySelector('.gift-modal');
      if (!modal) return;
      
      const allFocusable = modal.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const focusableElements = [...allFocusable].filter((el) => {
        if (el.hidden || el.closest('[hidden]')) return false;
        if (el.offsetParent === null && !el.closest('[role="dialog"]')) return false;
        const step = el.closest('.gift-modal__step');
        if (step && !step.classList.contains('is-active-step')) return false;
        return true;
      });

      if (focusableElements.length === 0) return;

      const firstEl = focusableElements[0];
      const lastEl = focusableElements[focusableElements.length - 1];
      
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
  }
  
  handleViewportResize() {
    if (!this.isOpen || !window.visualViewport) return;
    const overlay = this.querySelector('#gift-modal-overlay');
    const modal = this.querySelector('.gift-modal');
    if (!overlay || !modal) return;

    const vv = window.visualViewport;
    // 60px threshold (Vaul pattern): address bar collapse is ~44px,
    // keyboard is ~250-350px. This avoids false positives.
    const diff = (this._baseViewportHeight || window.innerHeight) - vv.height;

    if (diff > 60) {
      const active = document.activeElement;
      if (!modal.contains(active) || !active.matches('textarea, input[type="text"]')) {
        this._clearKeyboardStyles();
        return;
      }
      // Keyboard is open.
      // Translate the overlay so it tracks the visual viewport, but keep
      // its height at 100% so the dark backdrop covers the full screen
      // (the keyboard itself hides what's below the modal).
      overlay.style.transform = `translateY(${vv.offsetTop}px)`;
      modal.style.height = `${vv.height}px`;

      // No extra bottom padding here: shrinking the modal to the visual
      // viewport already removed the keyboard's height from the body, and
      // padding it again leaves the editor with no height on iPhones.

      // Hide the sticky footer while the keyboard is up — it eats ~70px
      // of the already cramped visible area.  Close/Save aren't needed
      // while typing; they reappear the moment the keyboard closes.
      const footer = this.querySelector('.gift-modal__footer');
      if (footer) footer.style.display = 'none';
    } else {
      // Keyboard closed — let CSS (100dvh) handle address-bar changes.
      this._clearKeyboardStyles();
      this._baseViewportHeight = vv.height;
    }
  }

  // Scroll the focused input into view *within the modal's own scroll
  // container* (`.gift-modal__body` on mobile).  Using the browser's
  // `scrollIntoView()` on iOS causes the page to jump because the
  // overlay is `position: fixed`.  Instead we calculate the input's
  // offset relative to the scroll container and scroll that container
  // directly.  If no scroll container is found (desktop sidebar layout),
  // we fall back to a no-op because desktop doesn't need this help.
  // ── iOS keyboard scroll fix ──
  // HARD-WON LESSON (Feb 2026, ~6 iterations):
  //
  // Problem: On iOS Safari/Chrome, tapping a form field (especially "From",
  // the last field) in a position:fixed modal doesn't reliably scroll it
  // into view when the software keyboard opens.
  //
  // What DIDN'T work:
  //  1. Manual getBoundingClientRect + scrollTop math — too many timing
  //     failure modes during the ~300ms iOS keyboard animation. Measurements
  //     taken mid-animation are stale by the time scrollTop is set.
  //  2. 100dvh for modal height — dvh does NOT track the iOS keyboard,
  //     only visualViewport.height does.
  //  3. position:fixed + overflow:hidden for scroll lock — WebKit bug
  //     #153852 means hidden still allows scroll. Need overflow:clip.
  //
  // What DOES work:
  //  - overflow:clip on html/body (via .gift-scroll-locked) prevents page
  //    scroll entirely, so scrollIntoView can only scroll within
  //    .gift-modal__body — no page jump risk.
  //  - visualViewport resize listener with 300ms debounce (matches keyboard
  //    animation duration) ensures we scroll AFTER layout is final.
  //  - Hide the sticky footer when keyboard is open (saves ~70px of space).
  //  - Shrinking the modal to visualViewport.height is the one keyboard
  //    compensation. Also padding the body by the keyboard height counts it
  //    twice and blanks the editor on iPhones.
  //
  // See also: ai/skills/storefront-ops/references/lessons/ios-keyboard-scroll.md
  _scrollToInput(input) {
    if (!input || !input.isConnected) return;

    // Cancel any in-flight scroll from a prior focusin (M2/M3: prevents
    // listener leak and race condition on rapid focus switching).
    this._scrollCleanup?.();

    // Use the browser's native scrollIntoView instead of manual
    // getBoundingClientRect math.  With overflow:clip on html/body
    // (gift-scroll-locked), scrollIntoView can only scroll within
    // .gift-modal__body — no page jump risk.
    const doScroll = () => {
      if (!input.isConnected) return;
      input.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };

    // Wait for the iOS keyboard animation to fully settle before scrolling.
    // The keyboard takes ~250-300ms to animate; we debounce resize events
    // and fire after 300ms of quiet to ensure layout is final.
    const vv = window.visualViewport;
    if (!vv) {
      setTimeout(doScroll, 300);
      return;
    }

    let didScroll = false;
    let sawResize = false;
    let settleTimer;
    let fallbackTimer;

    const cleanup = () => {
      vv.removeEventListener('resize', onResize);
      clearTimeout(settleTimer);
      clearTimeout(fallbackTimer);
      this._scrollCleanup = null;
    };

    const scrollOnce = () => {
      if (didScroll) return;
      didScroll = true;
      cleanup();
      requestAnimationFrame(doScroll);
    };

    const onResize = () => {
      sawResize = true;
      clearTimeout(settleTimer);
      // 300ms settle: matches iOS keyboard animation duration (~250-300ms)
      settleTimer = setTimeout(scrollOnce, 300);
    };

    vv.addEventListener('resize', onResize);
    this._scrollCleanup = cleanup;

    // If the keyboard was already open (e.g. tabbing between fields),
    // no resize fires. Scroll after a short delay.
    fallbackTimer = setTimeout(() => {
      if (!sawResize) scrollOnce();
    }, 400);
  }

  // Mobile segmented control - switch between front/back preview
  switchPreviewSide(side) {
    this.activePreviewSide = side;
    
    // Update tabs
    this.querySelectorAll('[data-preview-tab]').forEach(tab => {
      const isActive = tab.dataset.previewTab === side;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    
    // Update panels (cross-fade 180ms via CSS)
    this.querySelectorAll('[data-preview-panel]').forEach(panel => {
      const isActive = panel.dataset.previewPanel === side;
      panel.classList.toggle('is-active', isActive);
    });
  }
  
  handleCardChange(e) {
    this.selectedCardId = e.target.value;
    // Reset front-of-card selections to new card's defaults
    const card = CARD_CATALOG_BY_ID[this.selectedCardId];
    this.selectedGreeting = card?.greetingPresets?.[0] || '';
    this.selectedClosing = card?.closingPresets?.[0] || '';
    this.renderPresetChips();
    this.updateIllustratorCredit(card);
  }

  updateIllustratorCredit(card) {
    const el = this.querySelector('#illustrator-credit');
    if (!el) return;
    if (!card?.artist) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = `${translate('pages.gift_modal.illustrated_by')} `;
    if (card.artist.url) {
      const link = document.createElement('a');
      link.href = card.artist.url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.className = 'gift-modal__illustrator-link';
      link.textContent = card.artist.name;
      el.appendChild(link);
    } else {
      el.appendChild(document.createTextNode(card.artist.name));
    }
  }

  handleRecipientInput(e) {
    this.recipientName = e.target.value;
    this.updatePreview();
  }
  
  renderOccasionStarters() {
    const group = this.querySelector('[data-starter-group]');
    if (!group || !Catalog.MESSAGE_STARTERS) return;
    group.innerHTML = Catalog.MESSAGE_STARTERS.map((starter) => {
      const localized = localizedStarter(starter);
      return `
      <button
        type="button"
        class="gift-modal__chip${this.lastStarterId === starter.id ? ' is-selected' : ''}"
        data-starter-id="${starter.id}"
        aria-pressed="${this.lastStarterId === starter.id ? 'true' : 'false'}"
      >${this.escapeHtml(localized.label)}</button>
    `;
    }).join('');
  }

  updateMatchHelper() {
    const helper = this.querySelector('[data-match-helper]');
    if (!helper) return;
    const match =
      Gift.flavorCardMatchForHandle &&
      Gift.flavorCardMatchForHandle(
        this.callerTrigger?.productHandle || this.callerTrigger?.dataset?.productHandle || ''
      );
    if (match && this.selectedCardId === match.id) {
      helper.hidden = false;
      helper.textContent = translate(
        'pages.gift_modal.matched_card',
        { card: localizedCardLabel(match) },
      );
    } else {
      helper.hidden = true;
      helper.textContent = '';
    }
  }

  handleStarterClick(e) {
    const btn = e.target.closest('[data-starter-id]');
    if (!btn) return;
    if (this.applyOccasionStarter(btn.dataset.starterId)) return;

    const starter = Catalog.starterById?.(btn.dataset.starterId);
    if (!starter) return;
    const replace = window.confirm(translate('pages.gift_modal.replace_starter_confirm'));
    if (!replace) return;
    this.noteText = localizedStarter(starter).message;
    this.lastStarterId = starter.id;
    const noteEl = this.querySelector('#gift-note');
    if (noteEl) noteEl.value = this.noteText;
    this.updateCharCount();
    this.updatePreview();
    this.renderOccasionStarters();
  }

  applyOccasionStarter(starterId) {
    const next = Catalog.starterById?.(starterId);
    if (!next) return false;
    const previous = this.lastStarterId ? Catalog.starterById?.(this.lastStarterId) : null;
    const previousLocalized = previous ? localizedStarter(previous) : null;
    const current = typeof this.noteText === 'string' ? this.noteText : '';
    const untouched = current.trim() === '' || (
      previous && [previous.message, previousLocalized?.message].includes(current)
    );
    if (!untouched) return false;
    this.noteText = localizedStarter(next).message;
    this.lastStarterId = next.id;
    const noteEl = this.querySelector('#gift-note');
    if (noteEl) noteEl.value = this.noteText;
    this.updateCharCount();
    this.updatePreview();
    this.renderOccasionStarters();
    return true;
  }

  handleNoteInput(e) {
    this.noteText = e.target.value;
    this.updateCharCount();
    this.updatePreview();
    if (this.callerTrigger?.updateState) {
      this.callerTrigger.updateState();
    } else {
      Gift.updateGiftNoteCardState?.();
    }
  }
  
  handleSenderInput(e) {
    this.senderName = e.target.value;
    this.updatePreview();
  }
  
  handleFontChange(e) {
    this.selectedFont = normalizeFontId(e.target.value);
    this.updatePreview();
  }
  
  updateCharCount() {
    const count = this.noteText.length;
    const remaining = this.maxChars - count;
    const charCountEl = this.querySelector('#char-count');
    
    if (charCountEl) {
      charCountEl.textContent = `${count}/${this.maxChars}`;
      charCountEl.hidden = remaining > this.maxChars * 0.2;
      charCountEl.classList.remove('is-near-limit', 'is-at-limit');
      if (remaining <= 0) {
        charCountEl.classList.add('is-at-limit');
      } else if (remaining <= this.maxChars * 0.2) {
        charCountEl.classList.add('is-near-limit');
      }
    }
  }
  
  updatePreview() {
    // Font class helper — must be defined before first use
    const fontClasses = ['is-modern', 'is-serif'];
    const applyFontClass = (el) => {
      if (!el) return;
      el.classList.remove(...fontClasses);
      el.classList.add(`is-${this.selectedFont}`);
    };

    // Update front card
    const frontCard = this.querySelector('#card-front');
    const frontGreeting = this.querySelector('#front-greeting');
    const frontSubtext = this.querySelector('.gift-modal__card-front-subtext');
    if (!CARD_CATALOG_BY_ID[this.selectedCardId]) {
      this.selectedCardId = DEFAULT_CARD_ID;
      const fallbackInput =
        this.querySelector(`input[name="cardId"][value="${this.selectedCardId}"]`);
      if (fallbackInput) fallbackInput.checked = true;
    }
    const card = CARD_CATALOG_BY_ID[this.selectedCardId];

    if (frontCard) {
      frontCard.classList.forEach((className) => {
        if (className.startsWith('is-')) frontCard.classList.remove(className);
      });
      frontCard.classList.add(`is-${this.selectedCardId}`);
    }

    // Handle artwork image
    const frontArt = this.querySelector('#front-art');
    if (frontArt) {
      if (card?.imageUrl) {
        if (this.imagesHydrated) applyCardImage(frontArt, card, 'front');
        else applyDeferredCardImage(frontArt, card, 'front');
        frontArt.alt = localizedCardImageAlt(card);
        frontArt.hidden = false;
        // Apply per-card image sizing
        frontArt.style.maxHeight = card.imageStyle?.maxHeight || '';
        frontArt.style.marginTop = card.imageStyle?.marginTop || '';
      } else {
        frontArt.hidden = true;
      }
    }
    if (frontGreeting) {
      const greetingIndex = (card?.greetingPresets || []).indexOf(this.selectedGreeting);
      frontGreeting.textContent = this.selectedGreeting
        ? greetingIndex >= 0
          ? localizedCardPreset(card, 'greeting', greetingIndex, this.selectedGreeting)
          : this.selectedGreeting
        : localizedCardPreset(card, 'greeting', 0, card?.greetingPresets?.[0] || '');
      applyFontClass(frontGreeting);
    }
    if (frontSubtext) {
      const closingIndex = (card?.closingPresets || []).indexOf(this.selectedClosing);
      frontSubtext.textContent = this.selectedClosing
        ? closingIndex >= 0
          ? localizedCardPreset(card, 'closing', closingIndex, this.selectedClosing)
          : this.selectedClosing
        : localizedCardPreset(card, 'closing', 0, card?.closingPresets?.[0] || '');
      applyFontClass(frontSubtext);
    }

    // Update summary row (Step 2 header)
    const summaryIcon = this.querySelector('#summary-icon');
    const summaryLabel = this.querySelector('#summary-label');

    if (summaryIcon) {
      if (card?.imageUrl && this._summaryImg) {
        // Reuse persistent image node — just update src/alt instead of recreating.
        if (this.imagesHydrated) applyCardImage(this._summaryImg, card, 'summary');
        else applyDeferredCardImage(this._summaryImg, card, 'summary');
        this._summaryImg.alt = localizedCardImageAlt(card);
        summaryIcon.replaceChildren(this._summaryImg);
      } else {
        summaryIcon.textContent = localizedCardLabel(card);
      }
    }
    if (summaryLabel && card) summaryLabel.textContent = localizedCardLabel(card);

    // Update summary row aria-label
    const summaryBtn = this.querySelector('#card-summary');
    if (summaryBtn && card) {
      summaryBtn.setAttribute('aria-label', translate('pages.gift_modal.card_selected', {
        card: localizedCardLabel(card),
        action: translate('pages.gift_modal.choose_another_design'),
      }));
    }

    // Update recipient
    const recipientEl = this.querySelector('#preview-recipient');
    const recipientRow = recipientEl?.closest('.gift-modal__card-recipient');
    const trimmedRecipient = this.recipientName.trim();
    if (recipientEl) {
      recipientEl.textContent = trimmedRecipient;
      applyFontClass(recipientEl);
    }
    if (recipientRow) {
      recipientRow.hidden = !trimmedRecipient;
      applyFontClass(recipientRow);
    }
    
    // Update message
    const messageEl = this.querySelector('#preview-message');
    if (messageEl) {
      applyFontClass(messageEl);
      
      if (this.noteText.trim()) {
        messageEl.innerHTML = this.escapeHtml(this.noteText);
      } else {
        messageEl.innerHTML = `<span class="gift-modal__card-message-placeholder">${this.escapeHtml(translate('pages.gift_modal.note_placeholder'))}</span>`;
      }
    }
    
    // Update sender
    const senderEl = this.querySelector('#preview-sender');
    if (senderEl) {
      const trimmedSender = this.senderName.trim();
      if (trimmedSender) {
        senderEl.textContent = `— ${trimmedSender}`;
        senderEl.hidden = false;
      } else {
        senderEl.textContent = '';
        senderEl.hidden = true;
      }
      applyFontClass(senderEl);
    }
  }
  
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/\n/g, '<br>');
  }

  escapeAttr(text) {
    return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  
  // Validate form fields - returns error message or null if valid
  validateForm() {
    const message = this.noteText.trim();

    // Message length check
    if (message.length > this.maxChars) {
      return {
        field: 'gift-note',
        message: translate('pages.gift_modal.error_message_length', { count: this.maxChars })
      };
    }

    // Greeting length check
    if (this.selectedGreeting.length > GREETING_MAX_LENGTH) {
      return {
        field: null,
        message: translate('pages.gift_modal.error_headline_length', { count: GREETING_MAX_LENGTH })
      };
    }

    // Closing length check
    if (this.selectedClosing.length > CLOSING_MAX_LENGTH) {
      return {
        field: null,
        message: translate('pages.gift_modal.error_accent_length', { count: CLOSING_MAX_LENGTH })
      };
    }

    // Card validation (guard against invalid state)
    const validCards = Object.keys(CARD_CATALOG_BY_ID);
    if (!validCards.includes(this.selectedCardId)) {
      return {
        field: null,
        message: translate('pages.gift_modal.error_pick_card')
      };
    }

    return null;
  }

  handleHandwrite() {
    // This path deliberately keeps the chosen card ID for fulfillment while
    // clearing every printable field. The regular submit path then injects the
    // explicit blank config into the exact originating form/cart line.
    this.selectedGreeting = '';
    this.selectedClosing = '';
    this.recipientName = '';
    this.noteText = '';
    this.senderName = '';
    this.setStep('write-message');
    this.handleSubmit();
  }
  
  handleSubmit() {
    // Step 1: "Next" advances to Step 2
    if (this.step === 'pick-card') {
      this.confirmCard();
      return;
    }

    // Debounce: prevent rapid double-taps
    const now = Date.now();
    if (now - this.lastSubmitTime < this.submitDebounceMs) {
      Gift.debug?.('Debounced double-submit');
      return;
    }
    this.lastSubmitTime = now;

    // In-flight lock: don't submit if already saving
    if (this.isSaving) {
      Gift.debug?.('Already saving, ignoring submit');
      return;
    }

    // Set saving lock and disable button BEFORE any async work (prevents TOCTOU double-click)
    this.isSaving = true;
    this.setSubmitButtonLoading(true);

    // Clear any previous errors
    this.clearError();

    // Validate
    const error = this.validateForm();
    if (error) {
      this.isSaving = false;
      this.setSubmitButtonLoading(false);
      this.showError(error.message);
      this.trackGiftAnalytics('GIFT_CUSTOMIZER_FAILED', {
        gift_mode: this.mode,
        failure_class: error.field ? 'field_validation' : 'configuration_validation',
      });
      // Focus the problematic field if specified
      if (error.field) {
        const field = this.querySelector(`#${error.field}`);
        if (field) {
          field.focus({ preventScroll: true });
          this._scrollToInput(field);
        }
      }
      return;
    }

    const card = CARD_CATALOG_BY_ID[this.selectedCardId] || CARD_CATALOG[0];
    let greeting = this.selectedGreeting.trim() || card.greetingPresets?.[0] || '';
    let closing = this.selectedClosing.trim() || card.closingPresets?.[0] || '';

    // A buyer who writes NOTHING must send a blank card, not this card's preset
    // copy. renderPresetChips() auto-selects presets[0] on open, so without this
    // an untouched submit shipped the lead card's slogan — a baby-shower or
    // sympathy buyer got "FOR THE WIN / Big Knicks Energy". Blank in, blank out.
    const wroteNothing = !this.recipientName.trim() && !this.senderName.trim() && !this.noteText.trim()
      && greeting === (card.greetingPresets?.[0] || '')
      && closing === (card.closingPresets?.[0] || '');
    if (wroteNothing) {
      greeting = '';
      closing = '';
    }

    // Build config object per Operation Santa spec
    const config = {
      schema: 'snowcubes.giftCard.v1',
      cardId: this.selectedCardId,
      frontGreeting: greeting,
      frontClosing: closing,
      to: this.recipientName.trim(),
      from: this.senderName.trim(),
      message: this.noteText.trim(),
      font: this.selectedFont
    };
    
    // Serialized-size guard: the per-field limits (message 200, to/from 50, …) can
    // still sum past the cart line-item property byte cap (GIFT_CONFIG_MAX_BYTES).
    // Without this the note is SILENTLY dropped — localStorage save + the /cart
    // property both reject the oversize value — while the toast says "added". Use
    // the same serializer as the save path so the limit stays single-source.
    const serializedConfig = Gift.serializeGiftConfig
      ? Gift.serializeGiftConfig(config)
      : { ok: true, value: JSON.stringify(config) };
    if (!serializedConfig.ok) {
      this.isSaving = false;
      this.setSubmitButtonLoading(false);
      this.showError(translate('pages.gift_modal.error_details_too_long'));
      this.trackGiftAnalytics('GIFT_CUSTOMIZER_FAILED', {
        gift_mode: this.mode,
        failure_class: 'serialized_size',
      });
      return;
    }
    const configJson = serializedConfig.value;

    Gift.debug?.('Gift Box Config', Gift.summarizeGiftConfig?.(config) || config);

    // "Edit" can mean two things:
    // 1) Pre-cart edit from PDP (no itemKey/variantId) → update product form inputs only
    // 2) Cart edit from cart drawer (itemKey/variantId provided) → /cart/change.js
    const isCartEdit = this.mode === 'edit' && (this.itemKey || this.variantId);

    // Cart edits have no product trigger. Saving prototype state first would fall
    // back to the current page's product handle and rehydrate this note onto a
    // different PDP (wrong recipient/message on the next gift-wrap add).
    if (!isCartEdit && window.giftConfigPrototype?.save) {
      const resumeHost =
        this.resumeProductForm?.closest?.('product-form') || this.resumeProductForm;
      const productHandle =
        this.callerTrigger?.productHandle ||
        this.callerTrigger?.dataset?.productHandle ||
        Gift.productHandleFromElement?.(this.callerTrigger) ||
        resumeHost?.dataset?.productHandle ||
        Gift.productHandleFromElement?.(resumeHost) ||
        null;
      window.giftConfigPrototype.save(config, productHandle || undefined);
      Gift.debug?.('[Prototype] Saved gift config', {
        ...(Gift.summarizeGiftConfig?.(config) || {}),
        productHandle,
      });
    }

    if (isCartEdit) {
      // Cart edit mode: update cart via /cart/change.js
      this.saveChangesToCart(config);
      return;
    }

    // Add / pre-cart edit: inject hidden inputs into product form
    this.isSaving = false;
    this.setSubmitButtonLoading(false);
    const injectionResult = this.injectConfigToProductForm(configJson, config);
    if (!injectionResult.ok) {
      this.isSaving = false;
      this.setSubmitButtonLoading(false);
      this.showError(translate('pages.gift_modal.error_save'));
      this.trackGiftAnalytics('GIFT_CUSTOMIZER_FAILED', {
        gift_mode: this.mode,
        failure_class: injectionResult.error || 'product_form_injection',
      });
      return;
    }
    // Capture trigger before close() clears it
    const trigger = this.callerTrigger;
    const resumeProductForm = this.resumeProductForm;
    const resumeSubmitter = this.resumeSubmitter;
    const shouldResumeAddToCart =
      this.resumeAddToCart &&
      resumeProductForm?.isConnected &&
      typeof resumeProductForm.requestSubmit === 'function';

    this.analyticsCompleted = true;
    this.trackGiftAnalytics('GIFT_CUSTOMIZER_COMPLETED', {
      gift_mode: this.mode,
      completion_surface: 'product_form',
      add_intent_resumed: shouldResumeAddToCart,
      entry_action: this.entryAction,
      has_recipient: !!config.to,
      has_sender: !!config.from,
      has_message: !!config.message,
      message_length_bucket: messageLengthBucket(config.message),
    });

    // Restore focus to the originating Add to cart control before resuming so
    // keyboard and assistive-technology users are never stranded in the hidden
    // modal. The existing cart success path may then move focus with the drawer.
    this.close(false, shouldResumeAddToCart);
    this.showFeedback(translate(
      this.mode === 'edit' ? 'pages.gift_modal.feedback_updated' : 'pages.gift_modal.feedback_saved',
    ));

    // Update card state — prefer trigger's scoped update, fall back to global
    if (trigger?.updateState) {
      trigger.updateState();
    } else {
      Gift.updateGiftNoteCardState?.();
    }
    
    // Dispatch event for any listeners
    this.dispatchEvent(new CustomEvent('gift-box-submit', {
      detail: { config, mode: this.mode },
      bubbles: true
    }));

    // When the modal was opened by an intercepted Add to cart submit, honor
    // that original intent instead of forcing a second click. The injected
    // config makes the resumed submit pass the product-form guard. Clear the
    // resume state first so an unexpected re-entry cannot loop.
    if (shouldResumeAddToCart) {
      if (resumeSubmitter) resumeProductForm.requestSubmit(resumeSubmitter);
      else resumeProductForm.requestSubmit();
    }
  }
  
  injectConfigToProductForm(configJson, config) {
    // An intercepted Add to cart must be resumed through the exact same form
    // that receives the config. Product pages can carry more than one cart
    // form (for example, the main PDP and a sticky/quick-add control), so a
    // trigger or document-wide resolver is not an equivalent substitute.
    let productForm = this.resumeProductForm?.isConnected ? this.resumeProductForm : null;

    if (productForm) {
      Gift.debug?.('Form resolved from intercepted add intent');
    }

    // Prefer trigger's scoped form resolution (OOP path)
    if (this.callerTrigger?.resolveProductForm) {
      if (!productForm) {
        productForm = this.callerTrigger.resolveProductForm();
        Gift.debug?.('Form resolved via trigger', {
          sectionId: this.callerTrigger.sectionId || null,
        });
      }
    }

    // Fallback: global DOM query (backward compat for non-trigger callers like cart drawer)
    if (!productForm) {
      const candidateForms = Array.from(document.querySelectorAll(
        'product-form form[action*="/cart/add"], product-form form'
      ));
      if (candidateForms.length === 1) {
        productForm = candidateForms[0];
      } else if (candidateForms.length > 1) {
        console.warn('Product form resolution is ambiguous; refusing a first-form fallback');
        return { ok: false, error: 'product_form_ambiguous' };
      }
    }

    if (!productForm) {
      console.error('Product form not found');
      return { ok: false, error: 'product_form_not_found' };
    }

    if (this.callerTrigger?.ensureGiftVariantSelected) {
      const variantResult = this.callerTrigger.ensureGiftVariantSelected();
      Gift.debug?.('Gift variant selection before form injection', {
        ok: !!variantResult?.ok,
        changed: !!variantResult?.changed,
        error: variantResult?.error || null,
        variantId: variantResult?.variant?.id || null,
      });
    }
    
    // Prefer core helper (single source of truth + unit-tested).
    if (Gift.upsertGiftInputs) {
      const result = Gift.upsertGiftInputs(productForm, config);
      if (!result.ok) {
        console.warn('Failed to serialize gift config for form:', result.error);
        return { ok: false, error: 'gift_input_serialization' };
      }
    } else {
      // Fallback (shouldn't run when core is loaded).
      const upsertInput = (name, value) => {
        let input = productForm.querySelector(`input[name="${name}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          productForm.appendChild(input);
        }
        input.value = value;
      };

      upsertInput('properties[_GiftCardConfig]', configJson);
      upsertInput('properties[_GiftCardTo]', config.to || '');
      upsertInput('properties[_GiftCardFrom]', config.from || '');
      upsertInput('properties[_GiftCardMessage]', config.message || '');
    }

    Gift.debug?.('Injected gift config into product form', {
      action: productForm.getAttribute('action') || '',
      ...Gift.summarizeGiftConfig?.(config),
    });
    return { ok: true };
  }
  
  // Cart operations are delegated to gift-modal-cart.js (window.SnowcubesGiftCart).
  // This thin wrapper preserves the class method API for handleSubmit.
  async saveChangesToCart(config) {
    const CartAPI = window.SnowcubesGiftCart;
    if (!CartAPI) {
      console.error('gift-modal-cart.js not loaded');
      this.isSaving = false;
      this.setSubmitButtonLoading(false);
      this.showError(translate('pages.gift_modal.error_save'));
      this.trackGiftAnalytics('GIFT_CUSTOMIZER_FAILED', {
        gift_mode: this.mode,
        failure_class: 'cart_api_unavailable',
      });
      return;
    }

    this.isSaving = true;
    this.setSubmitButtonLoading(true);

    const modal = this;
    const result = await CartAPI.saveChangesToCart({
      itemKey: this.itemKey,
      variantId: this.variantId,
      config: config,
      timeoutMs: this.saveTimeoutMs,
      callbacks: {
        onLoadingStart: () => {},
        onLoadingEnd: () => {
          modal.isSaving = false;
          modal.setSubmitButtonLoading(false);
        },
        onError: (msg) => {
          modal.showError(msg);
          modal.trackGiftAnalytics('GIFT_CUSTOMIZER_FAILED', {
            gift_mode: modal.mode,
            failure_class: 'cart_update',
          });
        },
        onSuccess: () => {
          modal.analyticsCompleted = true;
          modal.trackGiftAnalytics('GIFT_CUSTOMIZER_COMPLETED', {
            gift_mode: modal.mode,
            completion_surface: 'cart',
            has_recipient: !!config.to,
            has_sender: !!config.from,
            has_message: !!config.message,
            message_length_bucket: messageLengthBucket(config.message),
          });
          modal.close();
          modal.showFeedback(translate('pages.gift_modal.feedback_updated'));
        },
      },
    });

    // Ensure lock is released even if callbacks didn't fire
    this.isSaving = false;
    this.setSubmitButtonLoading(false);
  }
  
  // Visual loading state on submit button
  setSubmitButtonLoading(isLoading) {
    const btn = this.querySelector('#gift-modal-submit');
    const text = this.querySelector('#submit-btn-text');
    if (!btn || !text) return;
    
    if (isLoading) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      text.dataset.originalText = text.textContent;
      text.textContent = translate('pages.gift_modal.saving');
    } else {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      text.textContent = text.dataset.originalText || translate('pages.gift_modal.save_changes');
    }
  }
  
  // Clear any existing error message
  clearError() {
    const existingError = this.querySelector('.gift-modal__error');
    if (existingError) existingError.remove();
  }
  
  // Show error - calm, warm styling that fits the brand
  showError(message) {
    this.clearError();

    const errorEl = document.createElement('div');
    errorEl.className = 'gift-modal__error';
    errorEl.setAttribute('role', 'alert');
    errorEl.setAttribute('aria-live', 'polite');
    errorEl.textContent = message;
    this.querySelector('.gift-modal').appendChild(errorEl);

    // Auto-dismiss after reading time (but keep visible longer for network errors)
    const dismissTime = message.length > 60 ? 6000 : 4000;
    setTimeout(() => {
      if (errorEl.parentNode) {
        errorEl.classList.add('gift-modal__error--dismissing');
        setTimeout(() => errorEl.remove(), 300);
      }
    }, dismissTime);
  }
  
  showFeedback(message) {
    const toast = document.createElement('div');
    toast.className = 'gift-modal__toast';
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('gift-modal__toast--dismissing');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }
  
  // Push current state into DOM inputs (radios, text fields)
  syncFormToState() {
    this.querySelectorAll('input[name="cardId"]').forEach((input) => {
      input.checked = false;
    });
    const cardInput = this.querySelector(`input[name="cardId"][value="${this.selectedCardId}"]`);
    if (cardInput) cardInput.checked = true;

    const recipientEl = this.querySelector('#recipient-name');
    const noteEl = this.querySelector('#gift-note');
    const senderEl = this.querySelector('#sender-name');
    if (recipientEl) recipientEl.value = this.recipientName;
    if (noteEl) noteEl.value = this.noteText;
    if (senderEl) senderEl.value = this.senderName;

    const fontInput = this.querySelector(`input[name="font"][value="${this.selectedFont}"]`);
    if (fontInput) fontInput.checked = true;
  }

  syncMatchedCardPickerOption() {
    const covers = this.querySelector('[data-covers]');
    if (!covers) return;
    covers.querySelector('[data-matched-flavor-option]')?.remove();

    const card = CARD_CATALOG_BY_ID[this.matchedCardId];
    if (!card || card.family !== 'flavor') return;

    const label = document.createElement('label');
    label.className = 'gift-modal__cover-option';
    label.dataset.matchedFlavorOption = '';
    label.innerHTML = `
      <input type="radio" name="cardId" value="${card.id}">
      <div class="gift-modal__cover-label has-art" data-card-id="${card.id}">
        <img class="gift-modal__cover-img" ${getCardImageAttributes(card, 'cover')} data-card-image-id="${card.id}" data-card-image-variant="cover" alt="${this.escapeAttr(localizedCardImageAlt(card))}">
        <span class="gift-modal__cover-name">${this.escapeHtml(localizedCardLabel(card))}</span>
        <span class="gift-modal__cover-greeting">${this.escapeHtml(localizedCardPreset(card, 'greeting', 0, card.greetingPresets?.[0] || ''))}</span>
      </div>`;
    covers.prepend(label);
    label.querySelector('input').addEventListener('change', this.handleCardChange);
  }

  open(options = {}) {
    this.mode = options.mode || 'add';
    this.variantId = options.variantId || null;
    this.analyticsVariantId = options.analyticsVariantId || null;
    this.itemKey = options.itemKey || null;
    this.callerTrigger = options.trigger || null;
    this.resumeProductForm =
      options.resumeAddToCart && options.productForm instanceof HTMLFormElement
        ? options.productForm
        : null;
    this.resumeSubmitter =
      this.resumeProductForm &&
      options.submitter instanceof HTMLElement &&
      options.submitter.form === this.resumeProductForm
        ? options.submitter
        : null;
    this.resumeAddToCart = !!this.resumeProductForm;
    this.entryAction =
      options.entryAction ||
      (this.itemKey || this.variantId ? 'cart_edit' : 'customize_button');
    this.analyticsCompleted = false;
    this.lastStarterId = null;

    // A11y: Save currently focused element to restore on close
    this.previouslyFocusedElement = document.activeElement;
    
    // Reset any previous state
    this.isSaving = false;
    this.setSubmitButtonLoading(false);
    this.clearError();
    
    // Reset preview to front side (for mobile segmented control)
    this.switchPreviewSide('front');
    
    // Clear stale inline styles from a previous keyboard cycle
    this._clearKeyboardStyles();
    this._scrollCleanup?.();

    // Capture base viewport height before keyboard opens (for threshold calc).
    // Use innerHeight (layout viewport) — visualViewport.height could be
    // keyboard-reduced if opened while another field was focused.
    this._baseViewportHeight = window.innerHeight;

    // Set body flag for chat widget hiding etc.
    document.body.setAttribute('data-gift-modal-open', 'true');
    
    const titleText = this.querySelector('.gift-modal__title-text');
    titleText.textContent = this.mode === 'edit'
      ? translate('pages.gift_modal.title_edit')
      : translate('pages.gift_modal.title_add');

    const openingHandle =
      options.productHandle ||
      this.callerTrigger?.productHandle ||
      this.callerTrigger?.dataset?.productHandle ||
      '';
    this.matchedCardId =
      (Catalog.cardIdForProductHandle && Catalog.cardIdForProductHandle(openingHandle)) ||
      null;

    // The product-form path normally hands the saved draft in as `config`, but
    // the modal is lazy-loaded and can cross a runtime boundary before opening.
    // Re-read the exact handle-scoped draft here as the final safety boundary.
    // It remains a draft until the buyer explicitly submits this composer.
    const giftRuntime = window.SnowcubesGift || {};
    const draftStore = giftRuntime.giftConfigPrototype || window.giftConfigPrototype;
    const savedDraft =
      !options.config &&
      options.resumeAddToCart &&
      openingHandle
        ? draftStore?.load?.(openingHandle) || null
        : null;
    const openingConfig = options.config || savedDraft;

    // Populate state from config / prefill / defaults
    if (openingConfig) {
      this.selectedCardId = CARD_CATALOG_BY_ID[openingConfig.cardId]
        ? openingConfig.cardId
        : DEFAULT_CARD_ID;
      this.selectedGreeting = openingConfig.frontGreeting || '';
      this.selectedClosing = openingConfig.frontClosing || '';
      this.recipientName = openingConfig.to || '';
      this.noteText = openingConfig.message || '';
      this.senderName = openingConfig.from || '';
      this.selectedFont = normalizeFontId(openingConfig.font);
    } else if (options.prefill) {
      const prefillCardId = options.prefill.cardId || options.prefill.cover;
      this.selectedCardId = CARD_CATALOG_BY_ID[prefillCardId]
        ? prefillCardId
        : DEFAULT_CARD_ID;
      this.selectedGreeting = '';
      this.selectedClosing = '';
      this.recipientName = options.prefill.recipient || '';
      this.noteText = options.prefill.note || '';
      this.senderName = options.prefill.sender || '';
      this.selectedFont = normalizeFontId(options.prefill.font);
    } else {
      this.selectedCardId =
        this.matchedCardId ||
        DEFAULT_CARD_ID;
      this.selectedGreeting = '';
      this.selectedClosing = '';
      this.recipientName = '';
      this.noteText = '';
      this.lastStarterId = null;
      this.senderName = '';
      this.selectedFont = DEFAULT_FONT_ID;
    }

    this.syncMatchedCardPickerOption();
    this.syncFormToState();

    const quantityForm =
      this.resumeProductForm ||
      this.callerTrigger?.resolveProductForm?.() ||
      this.callerTrigger?.closest?.('product-form')?.querySelector('form');
    const quantity = Number.parseInt(quantityForm?.querySelector('[name="quantity"]')?.value || '1', 10);
    const quantityNote = this.querySelector('[data-gift-quantity-note]');
    if (quantityNote) {
      quantityNote.hidden = !(quantity > 1);
      quantityNote.textContent = quantity > 1
        ? translate('pages.gift_modal.quantity_note', { quantity })
        : '';
    }

    // Render preset chips (will auto-select defaults if greeting/closing empty)
    this.imagesHydrated = true;
    this.renderPresetChips();
    this.hydrateCardImages();
    this.updateCharCount();

    this.isOpen = true;
    this.querySelector('#gift-modal-overlay').classList.add('is-open');

    // Body scroll lock — overflow:clip approach.
    // The old `body { position: fixed; top: -scrollYpx }` pattern causes iOS
    // keyboard displacement: fixed-pos elements anchor to the layout viewport,
    // but the visual viewport shifts when the keyboard opens, making the modal
    // "ride up".  `overflow: clip` on html+body truly prevents all scrolling
    // (including iOS touch-through, unlike `overflow: hidden` — WebKit #153852)
    // without repositioning the body, so the keyboard has nothing to displace.
    // Supported in Safari 16+ (all 2026 iOS targets).
    this._savedScrollY = window.scrollY;
    document.documentElement.classList.add('gift-scroll-locked');

    // Mark background content as inert (a11y: prevents focus behind modal,
    // discourages iOS from scrolling background).
    const mainContent = document.getElementById('MainContent') || document.querySelector('main');
    if (mainContent) {
      mainContent.inert = true;
      this._inertTarget = mainContent;
    }

    // One-screen composer: both add and edit open on the note. Alternate
    // designs stay behind Choose another design.
    this.setStep('write-message');
    this.renderPresetChips();
    this.renderOccasionStarters();
    this.updateMatchHelper();
    requestAnimationFrame(() => {
      const scrollContainer = this.querySelector('.gift-modal__editor-scroll');
      if (scrollContainer) scrollContainer.scrollTop = 0;
    });
    this.trackGiftAnalytics('GIFT_CUSTOMIZER_OPENED', {
      gift_mode: this.mode,
      entry_surface: this.itemKey || this.variantId ? 'cart' : 'product',
      entry_action: this.entryAction,
    });
    this.trackGiftAnalytics('GIFT_CUSTOMIZER_STEP_VIEWED', {
      gift_mode: this.mode,
      gift_step: 'compose',
    });

    // A11y (WCAG 2.4.3): Move focus into the modal after it opens.
    // Use transitionend on the overlay to wait for visibility transition,
    // with a setTimeout fallback in case the event doesn't fire.
    const overlay = this.querySelector('#gift-modal-overlay');
    const focusModal = () => {
      if (!this.isOpen) return;
      const target = this.querySelector('.gift-modal__close');
      if (target) target.focus();
    };
    const onTransitionEnd = (e) => {
      if (e.propertyName === 'visibility' || e.propertyName === 'opacity') {
        overlay.removeEventListener('transitionend', onTransitionEnd);
        focusModal();
      }
    };
    if (overlay) overlay.addEventListener('transitionend', onTransitionEnd);
    // Fallback: focus after a generous delay if transitionend doesn't fire
    setTimeout(focusModal, 600);
  }
  
  close(force = false, restoreFocusImmediately = false) {
    // Prevent close while saving (unless forced)
    if (this.isSaving && !force) {
      Gift.debug?.('Cannot close while saving');
      return;
    }
    
    if (this.isOpen && !this.analyticsCompleted) {
      this.trackGiftAnalytics('GIFT_CUSTOMIZER_ABANDONED', {
        gift_mode: this.mode,
        gift_step: this.step === 'write-message' ? 'write_message' : 'pick_card',
        entry_action: this.entryAction,
      });
    }
    this.isOpen = false;
    this.callerTrigger = null;
    this.analyticsVariantId = null;
    this.resumeProductForm = null;
    this.resumeSubmitter = null;
    this.resumeAddToCart = false;
    this.querySelector('#gift-modal-overlay').classList.remove('is-open');

    // Clear inline styles from keyboard resize handler
    this._clearKeyboardStyles();
    this._scrollCleanup?.();

    // Restore body scroll — undo overflow:clip lock
    document.documentElement.classList.remove('gift-scroll-locked');
    if (this._inertTarget) {
      this._inertTarget.inert = false;
      this._inertTarget = null;
    }
    try {
      window.scrollTo(0, this._savedScrollY || 0);
    } catch {
      // Non-browser environments (e.g. JSDOM) may not implement scrollTo.
    }

    // Remove body flag
    document.body.removeAttribute('data-gift-modal-open');
    
    // A11y: Restore focus to previously focused element
    if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
      if (restoreFocusImmediately) {
        this.previouslyFocusedElement.focus();
      } else {
        setTimeout(() => {
          this.previouslyFocusedElement.focus();
        }, 100);
      }
    }
  }
}

customElements.define('gift-box-customizer-modal', GiftBoxCustomizerModal);

// Error + toast styles now live in gift-box-customizer-modal.css (no more CSS-in-JS).
// Global wiring (event delegation, DOMContentLoaded) lives in gift-modal-wiring.js.

  Gift.debug?.('Gift Box Customizer Modal class registered');
})();
